"use client";

import { createContext, useState, useContext, Dispatch, SetStateAction, useEffect } from "react";
import useBeforeUnload from "../hooks/useBeforeUnload";
import { usePathname } from "next/navigation";

type SetIsBlocked = Dispatch<SetStateAction<boolean>>;

const NavigationBlockerValue = createContext<boolean>(false);
const NavigationBlockerUpdater = createContext<SetIsBlocked>(() => {});

interface Props {
    children: React.ReactNode
}

/**
 * Context to control if a user should be warned before leaving the page.
 *
 * @see https://nextjs.org/docs/app/api-reference/components/link#blocking-navigation
 */
export default function NavigationBlockerProvider({ children }: Props) {
    const [isNavigationBlocked, setIsNavigationBlocked] = useState(false);

    const pathName = usePathname();

    useBeforeUnload(isNavigationBlocked);

    // If the user navigates even with the block warning in place, we want to remove the block warning.
    useEffect(() => {
        setIsNavigationBlocked((currentValue) => {
            if (currentValue === true) {
                return false;
            }

            return currentValue;
        });
    }, [pathName]);

    return (
        <NavigationBlockerUpdater.Provider value={setIsNavigationBlocked}>
            <NavigationBlockerValue.Provider value={isNavigationBlocked}>
                {children}
            </NavigationBlockerValue.Provider>
        </NavigationBlockerUpdater.Provider>
    );
}

export const useNavigationBlockerUpdater = () => {
    return useContext(NavigationBlockerUpdater)
}

export const useNavigationBlockerValue = () => {
    return useContext(NavigationBlockerValue)
}
